import { CalendarTenantScopeService } from '@modules/calendar-app/services/calendar-tenant-scope.service';
import { computed, DestroyRef, effect, inject, Injectable, LOCALE_ID, Signal, signal, untracked } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { type ItemsResponse } from '@modules/common/components/list/base-stitch-list.component';
import { SpaceContextStore } from '@modules/common/services/SpaceContextStore';
import { StitchEventsService } from '@modules/common/services/stitch-events-service';
import { Application } from '@modules/common/types/application';
import { type KnowledgeFlags } from '@modules/common/types/knowledge-flags';
import { DisplayTimeZoneService } from '@modules/common/services/display-timezone.service';
import { SignalUtils } from '@modules/common/utils/SignalUtils';
import { AdvancedSearchService } from '@modules/search/services/advanced-search.service';
import { getErrorMessage } from '@frontendlib/common/utils/ErrorMessage';
import { patchState, signalStore, withComputed, withHooks, withMethods, withProps, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { SignalStitchEventType } from '@sharedlib/common/types/signal-type';
import { StitchType } from '@sharedlib/common/types/StitchType';
import type { StitchSignalEventPayload } from '@sharedlib/dtos/dto/events/StitchSignalEventPayload';
import type { CalendarEventSummaryDto } from '@sharedlib/dtos/dto/calendar-app/event.dto';
import { catchError, EMPTY, filter, map, of, pairwise, pipe, switchMap, take, tap, type Observable } from 'rxjs';
import { CalendarType } from '../types/calendar-type';
import { EventsFilters } from '../types/events-filters';
import { beginningOfPeriodInZone, endOfPeriodInZone } from '../utils/display-zone-period.util';
import { signalCarriesNoChanges } from '../utils/event-rsvp.util';
import { EventsListStateService } from './events-list-state.service';
import { EventsService } from './events.service';
import { CalendarAppStateService } from './state.service';

type DateRangeCalendarEventsListState = {
  events: ItemsResponse<CalendarEventSummaryDto>;
  selectedDate: Date;
  scrollToDate: Date | null;
  knowledgeFlags: KnowledgeFlags | null;
  isLoading: boolean;
  error: string | null;
};

const initialState: DateRangeCalendarEventsListState = {
  events: {
    items: [],
    count: 0,
  },
  selectedDate: new Date(),
  scrollToDate: new Date(),
  knowledgeFlags: null,
  isLoading: true,
  error: null,
};

@Injectable()
export class DateRangeCalendarEventsListStore extends signalStore(
  withState(initialState),
  withProps((store) => {
    const scope = inject(CalendarTenantScopeService, { optional: true });
    const space = inject(SpaceContextStore);
    return {
      _ownerTenantId: computed(() => (scope ? scope.effectiveTenantId() : space.contextId())),
      _calendarAppStateService: inject(CalendarAppStateService),
      // When set, overrides the global calendar type for this store instance only.
      // Lets a consumer (e.g. the events panel List tab) pin the range to MONTH
      // without affecting the shared calendar grid.
      _calendarType: signal<CalendarType | null>(null),
      isLoading$: toObservable(store.isLoading),
    };
  }),
  withMethods((store) => {
    const eventsListStateService = inject(EventsListStateService);
    const advancedSearchService = inject(AdvancedSearchService);
    const localeId = signal(inject(LOCALE_ID));
    // SBUX-829 N1: the range must be computed in the display (account) zone, matching the grid.
    const displayTimeZoneService = inject(DisplayTimeZoneService);
    const displayTimeZone = displayTimeZoneService.timeZone;
    // ...and no query may be built before that zone is the real answer — see `currentFilters`.
    const displayTimeZoneResolved = displayTimeZoneService.isResolved;

    const globalCalendarType = toSignal(store._calendarAppStateService.calendarType.get(), {
      initialValue: store._calendarAppStateService.calendarType.getSync(),
    });
    // Prefer the per-instance override when present (see _calendarType).
    const calendarType: Signal<CalendarType> = computed(() => store._calendarType() ?? globalCalendarType());

    const scope = inject(CalendarTenantScopeService, { optional: true });
    const settledTenant = toSignal(scope?.selectedTeamId() ?? of(null));
    const tenantResolved = computed(() => settledTenant() !== undefined);
    const listState = toSignal(eventsListStateService.value());
    const searchState = toSignal(advancedSearchService.getState());
    const virtualFolder = toSignal(store._calendarAppStateService.getVirtualFolder());
    // `initialValue` matters: the source starts with `toObservable(contextId)`, which never emits
    // synchronously, so without it this signal is `undefined` on the first read and `currentFilters`
    // throws on `.length` — inside a view effect, which unwinds the rest of that change-detection pass.
    const selectedCalendarsUids = toSignal(store._calendarAppStateService.getSelectedCalendarUids(), {
      initialValue: [] as string[],
    });

    return {
      calendarType,
      tenantResolved,
      selectedCalendarsUids,
      listState,
      searchState,
      virtualFolder,
      localeId,
      displayTimeZone,
      displayTimeZoneResolved,

      setSelectedDate(date: Date, scrollToDate: Date | null = null): void {
        const newDate = new Date(date);
        patchState(store, { selectedDate: newDate, scrollToDate });
      },

      setCalendarType(type: CalendarType | null): void {
        store._calendarType.set(type);
      },

      setScrollToDate(date: Date | null): void {
        patchState(store, { scrollToDate: date ? new Date(date) : null });
      },
    };
  }),
  withComputed((store) => ({
    fromTime: computed(
      SignalUtils.memoizeDate((): Date => {
        return beginningOfPeriodInZone(
          store.selectedDate(),
          store.calendarType(),
          store.localeId(),
          store.displayTimeZone(),
        );
      }),
    ),
    toTime: computed(
      SignalUtils.memoizeDate((): Date => {
        return endOfPeriodInZone(store.selectedDate(), store.calendarType(), store.localeId(), store.displayTimeZone());
      }),
    ),
  })),
  withComputed((store) => ({
    currentFilters: computed((): EventsFilters | null => {
      store.selectedDate(); // track changes
      const listState = store.listState();
      const searchState = store.searchState();
      const virtualFolder = store.virtualFolder();
      const selectedCalendarUids = store.selectedCalendarsUids();

      if (!listState || !searchState || !store.tenantResolved()) {
        return null;
      }

      // SBUX-829: hold the FIRST query until the account timezone is known. `displayTimeZone` falls back
      // to the browser zone for rendering, so without this gate every page load fired an events request
      // reckoned in the DEVICE's zone (and labelled with it), then a corrected one ~180ms later. Returning
      // null is the store's existing "nothing to ask yet" signal, so the consumer already handles it.
      if (!store.displayTimeZoneResolved()) {
        return null;
      }

      if (virtualFolder !== 'all_events' || searchState.isAdvancedSearchApplied(Application.calendar)) {
        // time-range view is only relevant for "All Events" virtual folder without advanced filters, otherwise we rely on filters applied in listState and searchState
        return null;
      }

      /*
       * `displayTimeZone` is handed over at construction, not after the chain, so `applyListState` reckons
       * any day window it sets in the ACCOUNT zone rather than browser-locally — the rule every site that
       * builds a filters object follows; see devdoc/specs/today-filter.md. The grid boundaries below
       * replace whatever the list state put there today, so this changes nothing now. It stops the
       * omission from becoming a silent browser-zone bug the moment they stop replacing it, or a window
       * moves onto the activity pair, which `applyPickedWindow` clears and the three assignments below do
       * not restore.
       *
       * It also travels to the server, which is what lets it read the grid's edge days the way the grid
       * does: an ALL-DAY event is a floating calendar date and cannot be matched against instants,
       * SBUX-829 layer 2. Without it the first and last visible day lost their all-day events.
       */
      const filters: EventsFilters = new EventsFilters({
        containersIds: selectedCalendarUids,
        withEmpty: selectedCalendarUids.length === 0,
        displayTimeZone: store.displayTimeZone(),
      })
        .applyListState(listState)
        .applyAdvancedFilters(searchState)
        .applyVirtualFolder(virtualFolder);

      filters.withRecurrence = true;
      // Already the visible period's exact boundaries in the ACCOUNT zone, so they must not be widened
      // again browser-locally (SBUX-829). Set AFTER applyListState, which clears the flag on every pass,
      // and after applyAdvancedFilters, which clears it whenever it overwrites the bounds with a picked
      // range — so this assignment is the last word either way.
      filters.exactTimeRange = true;
      filters.fromTime = store.fromTime();
      filters.toTime = store.toTime();

      delete filters.pinnedOnTop;
      delete filters.flaggedOnTop;
      delete filters.snoozedOnTop;
      delete filters.followedOnTop;

      return filters;
    }),
  })),
  withMethods((store) => {
    const eventsService = inject(EventsService);

    const loadEventsRx = rxMethod<{
      readonly filters: EventsFilters;
      readonly knowledgeFlags: KnowledgeFlags | null;
      readonly tenantId: string | null | undefined;
    }>(
      pipe(
        tap((): void => {
          patchState(store, {
            isLoading: true,
            error: null,
          });
        }),
        switchMap(({ filters, knowledgeFlags, tenantId }) =>
          eventsService.search(filters, knowledgeFlags).pipe(
            filter(() => tenantId === store._ownerTenantId()),
            tap({
              next: (events: ItemsResponse<CalendarEventSummaryDto>): void => {
                patchState(store, { events, isLoading: false });
              },
              error: (error: Error): void => {
                if (tenantId === store._ownerTenantId())
                  patchState(store, { error: getErrorMessage(error), isLoading: false });
              },
            }),
            catchError(() => EMPTY),
          ),
        ),
      ),
    );

    return {
      waitForLoadToFinish(): Observable<void> {
        if (!store.isLoading()) {
          return of(void 0);
        }
        return store.isLoading$.pipe(
          filter((isLoading) => !isLoading),
          take(1),
          map(() => void 0),
        );
      },

      refresh(): void {
        const filters: EventsFilters | null = store.currentFilters();
        if (filters) {
          loadEventsRx({ filters, knowledgeFlags: store.knowledgeFlags(), tenantId: store._ownerTenantId() });
        }
      },
    };
  }),
  withHooks({
    onInit(store): void {
      const stitchEventsService = inject(StitchEventsService);
      const spaceContextStore = inject(SpaceContextStore);
      const calendarTenantScope = inject(CalendarTenantScopeService, { optional: true });
      const signalSpaceIds = () =>
        [spaceContextStore.contextId(), calendarTenantScope?.effectiveTenantId()].filter((id): id is string => !!id);
      const advancedSearchService = inject(AdvancedSearchService);
      const destroyRef = inject(DestroyRef);

      // EventsService already debounces collaboration updates and rechecks their owner.
      // One bridge per store lifetime; no second signal feed or debounce.
      inject(EventsService)
        .getRefresh()
        .pipe(takeUntilDestroyed(destroyRef))
        .subscribe(() => store.refresh());

      effect((): void => {
        store._ownerTenantId(); // reload even when the selected range/filters stay equal
        const filters = store.currentFilters();
        if (!filters) {
          return;
        }

        store.knowledgeFlags(); // track changes

        // untracked - because we want to control dependencies explicitly
        // and avoid re-running on unrelated state changes, if refresh(or loadEventsRx) reads some other state under the hood (potential infinite loop)
        untracked(() => {
          store.refresh();
        });
      });

      advancedSearchService
        .getState()
        .pipe(
          map((state) => state.isApplied()),
          pairwise(),
          filter(([prev, curr]) => prev === true && curr === false),
          takeUntilDestroyed(destroyRef),
        )
        .subscribe(() => {
          const currentDate = new Date();
          store.setSelectedDate(currentDate, currentDate);
        });

      store._calendarAppStateService
        .getVirtualFolder()
        .pipe(
          pairwise(),
          filter(([prev, curr]) => prev !== curr),
          takeUntilDestroyed(destroyRef),
        )
        .subscribe(() => {
          const currentDate = new Date();
          store.setSelectedDate(currentDate, currentDate);
        });

      stitchEventsService
        .getEvents(signalSpaceIds, StitchType.EVENT, [
          SignalStitchEventType.DELETED,
          SignalStitchEventType.CREATED,
          SignalStitchEventType.ARCHIVED,
          SignalStitchEventType.UNARCHIVED,
          SignalStitchEventType.MOVED_TO_TRASH,
          SignalStitchEventType.RESTORED_FROM_TRASH,
          SignalStitchEventType.SNOOZED,
          SignalStitchEventType.UNSNOOZED,
          SignalStitchEventType.SNOOZE_TIMEOUT,
        ])
        .pipe(takeUntilDestroyed(destroyRef))
        .subscribe(() => store.refresh());

      const isRecurrenceChange = (
        payload: StitchSignalEventPayload<StitchType.EVENT, SignalStitchEventType.UPDATED>,
      ): boolean =>
        'recurrence' in payload.changes &&
        payload.changes.startTime &&
        payload.changes.startTime.getTime() < store.toTime().getTime();

      const isCalendarMoveInChange = (
        payload: StitchSignalEventPayload<StitchType.EVENT, SignalStitchEventType.UPDATED>,
      ): boolean =>
        'calendarId' in payload.changes &&
        store.selectedCalendarsUids().some((uid) => uid === payload.changes.calendarId);

      const isCalendarMoveOutChange = (
        payload: StitchSignalEventPayload<StitchType.EVENT, SignalStitchEventType.UPDATED>,
      ): boolean =>
        'calendarId' in payload.changes &&
        !store.selectedCalendarsUids().some((uid) => uid === payload.changes.calendarId) &&
        store.events.items().some((event) => payload.ids.includes(event.id));

      // The `filter` and the branch below have to agree exactly: a term in one but not the other
      // either drops the signal or falls through to the in-place patch that cannot serve it.
      const needsFullRefresh = (
        payload: StitchSignalEventPayload<StitchType.EVENT, SignalStitchEventType.UPDATED>,
      ): boolean =>
        isRecurrenceChange(payload) ||
        isCalendarMoveInChange(payload) ||
        isCalendarMoveOutChange(payload) ||
        signalCarriesNoChanges(payload);

      stitchEventsService
        .getEvents(signalSpaceIds, StitchType.EVENT, [SignalStitchEventType.UPDATED])
        .pipe(
          filter(
            (payload) =>
              needsFullRefresh(payload) || store.events.items().some((event) => payload.ids.includes(event.id)),
          ),
          takeUntilDestroyed(destroyRef),
        )
        .subscribe((payload) => {
          if (needsFullRefresh(payload)) {
            store.refresh();
            return;
          }

          patchState(store, (state) => {
            return {
              events: {
                ...state.events,
                items: state.events.items.map((event) => {
                  if (payload.ids.includes(event.id)) {
                    event = { ...event, ...payload.changes };
                  }
                  return event;
                }),
              },
            };
          });
        });
    },
  }),
) {}
